import { useState, useEffect } from "react";
import SEO from "../seo";
import {
  PLANS_PRICING,
  STAFF_ACCOUNTS,
  TRANSACTION_FEES,
} from "../constant_data";

const ProPlan = () => {
  const [activeSection, setActiveSection] = useState("pricing");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["pricing", "features", "perfect-for"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <SEO
        title="xPage Pro Plan – Advanced Tools for Scaling Your Store"
        description="Explore xPage Pro Plan for growing businesses. Access advanced analytics, multi-staff collaboration, unlimited products, AI-powered landing pages, and reduced transaction fees."
        keywords="xPage Pro Plan, advanced ecommerce plan, landing page builder, AI tools, online store scaling, multi-staff support"
        canonical="https://help.xpage.ai/help/pricing-plans/Pro%20Plan"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold">Pro Plan</h1>
          <p className="text-sm text-systemSubTitle mt-1">
            xPage Pro Plan is built for growing businesses that need more
            advanced features, higher sales capacity, and greater operational
            flexibility. It's designed to give scaling merchants powerful tools
            for analytics, and multi-staff collaboration while keeping
            transaction fees low.
          </p>
        </div>

        {/* Pricing Details */}
        <div id="pricing" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Pricing
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-tag text-xl text-core mb-2"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-1">
                ${PLANS_PRICING.PRO.MONTHLY}
              </h3>
              <p className="text-systemLabel text-sm">per month</p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-credit-card text-xl text-core mb-2"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-1">
                Card Processing
              </h3>
              <p className="text-systemLabel text-sm">
                {TRANSACTION_FEES.PRO.CARD}% fee
              </p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-money-bill-wave text-xl text-core mb-2"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-1">
                COD Processing
              </h3>
              <p className="text-systemLabel text-sm">
                {TRANSACTION_FEES.PRO.COD}% fee
              </p>
            </div>
          </div>
        </div>

        {/* Features Table */}
        <div id="features" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            What's Included
          </h2>
          <p className="text-systemLabel text-sm mb-3">
            In addition to all the features available on all xPage plans, the
            Pro Plan includes:
          </p>

          <div className="bg-systemBackground border border-systemBorder rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-400/10 border-b border-systemBorder">
                  <th className="text-left p-3 text-systemTitle text-sm font-semibold">
                    Feature Category
                  </th>
                  <th className="text-left p-3 text-systemTitle text-sm font-semibold">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Core Features
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    AI-powered online landing page builder, unlimited AI landing
                    pages, unlimited products and collections, mobile-first
                    design, custom domain, 24/7 support
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Staff Accounts
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    {STAFF_ACCOUNTS.PRO} staff member accounts for expanded team
                    management
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Payment & Checkout
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Multiple gateways (Stripe, Paypal, Bancontact, COD & more),
                    high-converting checkout, reduced processing fees (
                    {TRANSACTION_FEES.PRO.CARD}% card rate,{" "}
                    {TRANSACTION_FEES.PRO.COD}% COD rate)
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Analytics
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Advanced analytics, real-time reports, event tracking for
                    deeper sales insights
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Marketing Tools
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Abandoned checkout recovery, discount codes, product
                    bundles, referral & affiliate program, unlimited contacts,
                    customer segmentation, advanced campaign targeting
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    International Selling
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Multi-language support, duties & import taxes calculation
                    for global commerce
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Hosting & Security
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Unlimited web hosting, free SSL certificate, secure
                    PCI-compliant payment processing
                  </td>
                </tr>
                <tr className="hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Threshold System
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Sales-based billing starting at a $15 threshold, increasing
                    dynamically as you grow; supports higher threshold limits
                    for established stores
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Who It's For Section */}
        <div id="perfect-for" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Who It's For
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-chart-line text-2xl text-core mb-3"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-2">
                Established Sellers
              </h3>
              <p className="text-systemLabel text-sm">
                Ready to scale operations with a larger team and advanced
                marketing tools.
              </p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-money-bill-trend-up text-2xl text-core mb-3"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-2">
                High Volume Businesses
              </h3>
              <p className="text-systemLabel text-sm">
                Managing higher transaction volumes and needing lower processing
                rates.
              </p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-globe text-2xl text-core mb-3"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-2">
                Global Expansion
              </h3>
              <p className="text-systemLabel text-sm">
                Businesses expanding into multiple markets with complex selling
                requirements.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#pricing"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "pricing"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Pricing
              </a>
            </li>
            <li>
              <a
                href="#features"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "features"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                What's Included
              </a>
            </li>
            <li>
              <a
                href="#perfect-for"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "perfect-for"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Who It's For
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ProPlan;
